import logoImageDark from "@/assets/global/logo-dark.webp";
import logoGrayImage from "@/assets/global/logo-gray.webp";
import logoIconDark from "@/assets/global/logo-icon-dark.webp";
import logoIcon from "@/assets/global/logo-icon.webp";
import logoImage from "@/assets/global/logo.webp";
import type { StaticImageData } from "next/image";

/**
 * 处理图片路径：如果是以 / 开头的相对路径，使用 OSS CDN URL 前缀拼接
 */
function processImagePath( path: string | undefined ): string | StaticImageData | undefined {
    if ( !path ) {
        return undefined;
    }

    const trimmed = path.trim();
    if ( !trimmed ) {
        return undefined;
    }

    // 如果以 / 开头，使用 OSS CDN URL 前缀拼接
    if ( trimmed.startsWith( "/" ) ) {
        const ossUrl = process.env.NEXT_PUBLIC_OSS_CDN_URL || "";
        return ossUrl ? `${ossUrl}${trimmed}` : trimmed;
    }

    // 其他情况（完整 URL）直接使用
    return trimmed;
}

/**
 * 处理 favicon 路径：如果是以 / 开头的相对路径，使用 OSS CDN URL 前缀拼接
 */
export function processFaviconPath( path: string | undefined ): string | undefined {
    if ( !path ) {
        return undefined;
    }

    const trimmed = path.trim();
    if ( !trimmed ) {
        return undefined;
    }

    // 如果以 / 开头，使用 OSS CDN URL 前缀拼接
    if ( trimmed.startsWith( "/" ) ) {
        const ossUrl = process.env.NEXT_PUBLIC_OSS_CDN_URL || "";
        return ossUrl ? `${ossUrl}${trimmed}` : trimmed;
    }

    // 其他情况（完整 URL）直接使用
    return trimmed;
}

/**
 * 获取品牌 Logo（首页左上）
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_LOGO，则使用环境变量值
 * 否则使用默认的静态导入图片
 */
export function getBrandLogo(): string | StaticImageData {
    const envLogo = process.env.NEXT_PUBLIC_BRAND_LOGO;
    if ( envLogo ) {
        const processed = processImagePath( envLogo );
        return processed || logoImage;
    }
    return logoImage;
}

/**
 * 获取暗色主题品牌 Logo（首页左上）
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_LOGO_DARK，则使用环境变量值
 * 否则使用默认的静态导入图片
 */
export function getBrandLogoDark(): string | StaticImageData {
    const envLogo = process.env.NEXT_PUBLIC_BRAND_LOGO_DARK;
    if ( envLogo ) {
        const processed = processImagePath( envLogo );
        return processed || logoImageDark;
    }
    return logoImageDark;
}

/**
 * 获取品牌灰色 Logo（详情页图例右侧）
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_LOGO_GRAY，则使用环境变量值
 * 否则使用默认的静态导入图片
 */
export function getBrandLogoGray(): string | StaticImageData {
    const envLogo = process.env.NEXT_PUBLIC_BRAND_LOGO_GRAY;
    if ( envLogo ) {
        const processed = processImagePath( envLogo );
        return processed || logoGrayImage;
    }
    return logoGrayImage;
}

/**
 * 获取品牌名称
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_NAME，则使用环境变量值
 * 否则使用默认值 "KREEZ"
 */
export function getBrandName(): string {
    return process.env.NEXT_PUBLIC_BRAND_NAME || "KREEZ";
}

/**
 * 替换文本中的品牌名称（统一处理所有大小写变体）
 * @param text 需要替换的文本
 * @returns 替换后的文本
 */
export function replaceBrandName( text: string ): string {
    const brandName = getBrandName();

    return text.replace( /KREEZ/g, brandName );
}

/**
 * 获取品牌 Logo Icon（用于帮助中心等场景）
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_LOGO_ICON，则使用环境变量值
 * 否则使用默认的静态导入图片
 */
export function getBrandLogoIcon(): string | StaticImageData {
    const envLogo = process.env.NEXT_PUBLIC_BRAND_LOGO_ICON;
    if ( envLogo ) {
        const processed = processImagePath( envLogo );
        return processed || logoIcon;
    }
    return logoIcon;
}

/**
 * 获取暗色主题品牌 Logo Icon（用于帮助中心等场景）
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_LOGO_ICON_DARK，则使用环境变量值
 * 否则使用默认的静态导入图片
 */
export function getBrandLogoIconDark(): string | StaticImageData {
    const envLogo = process.env.NEXT_PUBLIC_BRAND_LOGO_ICON_DARK;
    if ( envLogo ) {
        const processed = processImagePath( envLogo );
        return processed || logoIconDark;
    }
    return logoIconDark;
}

/**
 * 获取 favicon 路径
 * 如果设置了环境变量 NEXT_PUBLIC_BRAND_FAVICON，则使用环境变量值（处理后）
 * 否则返回 undefined（使用默认的 favicon.ico）
 */
export function getBrandFavicon(): string | undefined {
    const envFavicon = process.env.NEXT_PUBLIC_BRAND_FAVICON;
    if ( envFavicon ) {
        return processFaviconPath( envFavicon );
    }
    return undefined;
}
